import React, { useState, useEffect } from 'react';
import { useAdminApi } from '../../api/adminApi';
import {
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    TextField,
    MenuItem,
    Snackbar,
    Alert,
} from '@mui/material';
import { AssignmentInd as AssignmentIndIcon, PersonAdd as PersonAddIcon } from '@mui/icons-material';
import { AlertColor } from '@mui/material/Alert';

interface RoleUserDto {
    username: string;
    userId: string;
    email: string;
    firstName: string;
    lastName: string;
}

export const UserRolesPage = () => {
    const { getAllRoles, assignRoleToUser, revokeRoleFromUser, getUsersByRole } = useAdminApi();
    const [roles, setRoles] = useState<string[]>([]);
    const [selectedRole, setSelectedRole] = useState('');
    const [users, setUsers] = useState<RoleUserDto[]>([]);
    const [userId, setUserId] = useState('');
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: AlertColor;
    }>({ open: false, message: '', severity: 'success' });

    const loadRoles = async () => {
        const response = await getAllRoles();
        if (response.data) {
            setRoles(response.data.data);
        }
    };

    const loadUsersByRole = async (roleName: string) => {
        if (!roleName) return;
        const response = await getUsersByRole(roleName);
        if (response.data) {
            setUsers(response.data.data);
        }
    };

    useEffect(() => {
        loadRoles();
    }, []);

    useEffect(() => {
        loadUsersByRole(selectedRole);
    }, [selectedRole]);

    const handleAssignRole = async () => {
        try {
            const response = await assignRoleToUser(userId, selectedRole);

            // API'den gelen response'u kontrol et
            if (response.data && response.data.status === 'ok') {
                setSnackbar({ open: true, message: 'Rol başarıyla atandı!', severity: 'success' });
                loadUsersByRole(selectedRole);
                setUserId('');
            } else {
                // API başarılı dönmüş ama işlem başarısız olmuş
                const errorMessage = response.data?.errors?.[0]?.message || 'Rol atanamadı!';
                setSnackbar({ open: true, message: errorMessage, severity: 'error' });
            }
        } catch (error) {
            // Network hatası veya beklenmedik hata
            setSnackbar({ open: true, message: 'Rol atanırken hata oluştu!', severity: 'error' });
        }
    };

    const handleRevokeRole = async (userId: string) => {
        try {
            await revokeRoleFromUser(userId, selectedRole);
            setSnackbar({ open: true, message: 'Rol başarıyla kaldırıldı!', severity: 'success' });
            loadUsersByRole(selectedRole);
        } catch (error) {
            setSnackbar({ open: true, message: 'Rol kaldırılırken hata oluştu!', severity: 'error' });
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>Kullanıcı Rol Yönetimi</Typography>

            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <TextField
                    select
                    label="Rol Seçin"
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value)}
                    sx={{ minWidth: 200 }}
                >
                    {roles.map((role) => (
                        <MenuItem key={role} value={role}>{role}</MenuItem>
                    ))}
                </TextField>

                <TextField
                    label="Kullanıcı ID"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                    placeholder="Kullanıcı ID girin"
                />

                <Button
                    variant="contained"
                    startIcon={<PersonAddIcon />}
                    onClick={handleAssignRole}
                    disabled={!selectedRole || !userId}
                >
                    Rol Ata
                </Button>
            </Box>

            {selectedRole && (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Kullanıcı ID</TableCell>
                                <TableCell>Kullanıcı Adı</TableCell>
                                <TableCell>Ad</TableCell>
                                <TableCell>Soyad</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>İşlemler</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.map((user) => (
                                <TableRow key={user.userId}>
                                    <TableCell>{user.userId}</TableCell>
                                    <TableCell>{user.username}</TableCell>
                                    <TableCell>{user.firstName}</TableCell>
                                    <TableCell>{user.lastName}</TableCell>
                                    <TableCell>{user.email}</TableCell>
                                    <TableCell>
                                        <Button
                                            color="error"
                                            onClick={() => handleRevokeRole(user.userId)}
                                        >
                                            Rolü Kaldır
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
};